import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { Image as ImageIcon, MessageSquare, Paperclip, Pencil, Reply, Sparkles, X } from 'lucide-react';
import type { AiRequest, OutgoingAlbum, OutgoingMedia } from '../api/client.ts';
import { albumKindOf, nextPickId, roomFor, type PickedItem } from '../lib/album.ts';
import { canRecord, fileNameFor, type Recorded } from '../lib/capture.ts';
import { formatFileSize } from '../lib/format.ts';
import { bytesBehind, fileFromBlob, pastedMedia } from '../lib/paste.ts';
import { readVideoFrame, type VideoFrame } from '../lib/videoFrame.ts';
import { AlbumCompose } from './AlbumCompose.tsx';
import { AttachMenu } from './AttachMenu.tsx';
import { CameraScreen } from './CameraScreen.tsx';
import {
  findSmiley,
  parseMessage,
  smileyUrl,
  suggestSmileys,
  type Smiley,
} from '../lib/smileys.ts';
import { useSmileyBar } from '../lib/smileyBar.ts';
import { bumpSmiley, useSmileyStats } from '../lib/smileyStats.ts';
import { useKeyboardInset } from '../lib/useKeyboardInset.ts';
import { useIsMobile, useIsTouch } from '../lib/useMedia.ts';
import { useRecorder } from '../lib/useRecorder.ts';
import { RecordButtons, RecordPill, RecordPreview } from './RecordControl.tsx';
import { SmileyPanel } from './SmileyPanel.tsx';
import { SmileyPicker } from './SmileyPicker.tsx';
import { TrimScreen, type TrimRange } from './TrimScreen.tsx';
import './Composer.css';

/** Where a `:code` is being typed, so accepting a suggestion can replace it. */
interface CodeQuery {
  node: Text;
  start: number;
  end: number;
}

/**
 * The row above the input, per the handoff: the ten this reader uses most.
 * Whether it is there at all is theirs to say — see useSmileyBar.
 */
const QUICK_COUNT = 10;

/** A colon followed by word characters and no space — the handoff's pattern. */
const TYPED_CODE = /:([a-zA-Zа-яёА-ЯЁ0-9_-]{1,20})$/;

/** Builds the inline image a smiley becomes inside the input. */
function smileyImage(smiley: Smiley): HTMLImageElement {
  const img = document.createElement('img');
  img.src = smileyUrl(smiley.slug);
  img.alt = smiley.label;
  img.dataset.slug = smiley.slug;
  img.className = 'composer-smiley';
  return img;
}

/**
 * What a contentEditable puts a new line in. Chrome and Safari wrap each line
 * after the first in a `<div>`; a paste can bring `<p>` and list items along.
 * Firefox uses `<br>` instead, which is handled on its own below.
 */
const BLOCK = new Set(['DIV', 'P', 'LI', 'BLOCKQUOTE', 'PRE', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6']);

/**
 * Reads the composed message back out as text, with each smiley as its `:code:`
 * and each line the writer made as a newline.
 *
 * Walking the tree rather than the top-level children only: a paste, or the
 * browser's own editing, can leave the images nested inside an element.
 *
 * The two ways a browser writes a line break both have to come back as `\n`,
 * because a message is stored as plain text and has no other way to say it. The
 * `<br>` is Firefox's and the block element is Chrome's, and one document can
 * hold both \u2014 a Firefox `<br>` inside a pasted `<p>`. Entering a block adds the
 * break rather than leaving it, so nesting (`<div><div>a</div></div>`, which
 * Safari produces) yields one newline rather than one per level.
 */
function serialise(root: HTMLElement): string {
  let out = '';
  const walk = (node: Node) => {
    for (const child of node.childNodes) {
      if (child.nodeType === Node.TEXT_NODE) {
        out += child.nodeValue ?? '';
      } else if (child instanceof HTMLImageElement) {
        if (child.dataset.slug) out += `:${child.dataset.slug}:`;
      } else if (child instanceof HTMLBRElement) {
        out += '\n';
      } else {
        if (child instanceof HTMLElement && BLOCK.has(child.tagName) && out && !out.endsWith('\n')) {
          out += '\n';
        }
        walk(child);
      }
    }
  };
  walk(root);
  // The zero-width spaces below are scaffolding for the caret, never content.
  return out.replace(/\u200B/g, '').trim();
}

/**
 * What the clipboard has to say, as text.
 *
 * `text/uri-list` is the second place to look, and it is the iPhone's: «Скопи-
 * ровать ссылку» in another app can put a URL on the pasteboard with no plain
 * flavour at all, so a handler that reads only `text/plain` gets an empty string
 * and pastes nothing. Its format allows `#` comment lines, hence the filter.
 */
function pastedText(data: DataTransfer): string {
  const plain = data.getData('text/plain');
  if (plain) return plain.replace(/\r\n?/g, '\n');
  const uris = data.getData('text/uri-list').split(/\r\n?|\n/);
  return uris.find((line) => line !== '' && !line.startsWith('#')) ?? '';
}

/**
 * Puts text in at the caret. The fallback for a browser that refuses
 * `execCommand`, which is deprecated everywhere and gone in some places.
 */
function insertAtCaret(text: string): void {
  const selection = window.getSelection();
  if (!selection?.rangeCount) return;
  const range = selection.getRangeAt(0);
  range.deleteContents();
  const node = document.createTextNode(text);
  range.insertNode(node);
  range.setStartAfter(node);
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
}

/** The inverse of serialise: puts a stored message back into the input to edit. */
function deserialise(root: HTMLElement, body: string): void {
  root.innerHTML = '';
  for (const part of parseMessage(body)) {
    if (part.type === 'text') {
      root.appendChild(document.createTextNode(part.value));
    } else {
      const smiley = findSmiley(part.slug);
      if (smiley) root.appendChild(smileyImage(smiley));
    }
  }
}

const MODE_LABELS = { auto: 'Авто', chat: 'Чат', image: 'Картинка' } as const;

/** In this order because it is the order of commitment, cheapest first. */
const QUALITIES = [
  ['low', 'Черновик'],
  ['medium', 'Хорошее'],
  ['high', 'Максимум'],
] as const;

function ModeIcon({ mode }: { mode: 'auto' | 'chat' | 'image' }) {
  if (mode === 'chat') return <MessageSquare size={15} />;
  if (mode === 'image') return <ImageIcon size={15} />;
  return <Sparkles size={15} />;
}

/**
 * A little rectangle of the shape being asked for, beside its numbers.
 *
 * Drawn from the ratio rather than picked from a list of three, because the
 * list of shapes is the reader's own — «7:5» is a thing somebody can add — and
 * a glyph that only knew the built-in ones would quietly go blank on it. Capped
 * at 22px on the long side so a 9:16 and a 3:1 both stay inside the chip.
 */
function RatioGlyph({ ratio }: { ratio: string }) {
  const [w, h] = ratio.split(':').map(Number);
  const wide = Number.isFinite(w) && Number.isFinite(h) && w! > 0 && h! > 0 ? w! / h! : 1;
  const box = 16;
  return (
    <span
      className="composer-ratio-glyph"
      style={{
        width: `${wide >= 1 ? box : Math.round(box * wide)}px`,
        height: `${wide >= 1 ? Math.round(box / wide) : box}px`,
      }}
      aria-hidden="true"
    />
  );
}

/** One slash-command the chat answers, as the «/» panel lists it. */
export interface SlashCommand {
  command: string;
  description: string;
}

export function Composer({
  onSendText,
  onSendFile,
  onSendMedia,
  onSendAlbum,
  maxUploadBytes,
  onTyping,
  editing,
  replying,
  onSaveEdit,
  onCancelEdit,
  onCancelReply,
  commands,
  assistant,
}: {
  onSendText: (body: string, ai?: AiRequest) => void;
  onSendFile: (file: File) => void;
  /** A finished кружочек or voice message, encoded and ready to upload. */
  onSendMedia: (media: OutgoingMedia) => void;
  /** A group of photos and videos with one caption, from the compose screen. */
  onSendAlbum: (album: OutgoingAlbum) => void;
  /**
   * The server's per-attachment cap, delivered with the session. Handed down
   * rather than read from the session here, so this component stays a function
   * of its props — the same reason MessageThread is told whether this
   * deployment can transcribe anything.
   */
  maxUploadBytes: number;
  /** Every keystroke; what to make of them is decided in ChatContext. */
  onTyping: () => void;
  /** The message being rewritten, if any: its text is loaded into the input. */
  editing: { id: string; body: string } | null;
  /**
   * The message being answered, if any. Unlike an edit it does not touch the
   * input — whatever is half-written stays where it is and simply becomes a
   * reply — so all this shows is the banner above the bar. Which message it is
   * belongs to the screen; the sending call carries the id.
   */
  replying: { id: string; name: string; preview: string } | null;
  onSaveEdit: (id: string, body: string) => void;
  onCancelEdit: () => void;
  onCancelReply: () => void;
  /**
   * The slash-commands this chat answers, for the panel a leading «/» opens.
   * Absent everywhere but the assistant's chat — a «/» sent to a person is
   * just a character.
   */
  commands?: SlashCommand[];
  /**
   * Present only in an assistant's chat, and what puts the mode chip in the
   * field. Carries the shapes this person keeps and the tier they draw at by
   * default — both settings of theirs, not of the conversation.
   */
  assistant?: {
    provider: 'gemini' | 'openai';
    ratios: string[];
    quality: 'low' | 'medium' | 'high';
  };
}) {
  const [staged, setStaged] = useState<File | null>(null);
  const [canSend, setCanSend] = useState(false);
  /** A finished take waiting on the trim screen; null when there is none. */
  const [trimming, setTrimming] = useState<Recorded | null>(null);
  /**
   * The photos and videos picked so far. Non-empty is what puts the compose
   * screen up — there is no separate «is it open» to keep in step, and removing
   * the last item therefore closes it, which is what the handoff asks for.
   */
  const [album, setAlbum] = useState<PickedItem[]>([]);
  /** How many of the last pick did not fit under the cap, so it can explain itself. */
  const [dropped, setDropped] = useState(0);
  /** The one attach button's menu: camera, gallery and files, in one place. */
  const [attachOpen, setAttachOpen] = useState(false);
  /** The camera screen the menu's first option leads to — see CameraScreen. */
  const [cameraOpen, setCameraOpen] = useState(false);
  const [smileysOpen, setSmileysOpen] = useState(false);
  const [matches, setMatches] = useState<Smiley[]>([]);
  const [active, setActive] = useState(0);
  /** The commands the leading «/» currently matches; empty keeps the panel down. */
  const [commandMatches, setCommandMatches] = useState<SlashCommand[]>([]);

  /**
   * What the assistant is being asked for. Absent from every other chat.
   *
   * «Авто» is the resting state and stays collapsed into a chip inside the
   * field, adding no height at all — the composer in a bot's chat has to look
   * like the composer everywhere else until somebody wants it not to. Choosing
   * a mode is what opens the row; choosing «Картинка» is what opens the one
   * below it.
   *
   * Held here rather than lifted, because it is a property of what is about to
   * be typed and nothing outside this bar has an opinion about it. It survives
   * a send on purpose: three tries at one picture are the ordinary case, and
   * having to pick «Картинка» again each time would be the whole cost of the
   * feature.
   */
  const [mode, setMode] = useState<'auto' | 'chat' | 'image'>('auto');
  const [modeOpen, setModeOpen] = useState(false);

  /**
   * The shape and the tier are an override over the reader's settings, held as
   * «nothing chosen yet» rather than as a copy of the default.
   *
   * A copy would be seeded once, at mount — and the settings arrive from a
   * request that lands *after* the first render, so the seed would always be
   * the fallback and «Черновик» set on the settings screen would never reach
   * the composer. An effect that re-seeded on arrival would fix that and break
   * something else: it would also fire while somebody had «Максимум» chosen for
   * the picture they are about to ask for, and quietly take it back.
   *
   * Null until somebody picks, so the default follows the setting and a choice
   * made here outlives it for the rest of the session.
   */
  const [ratioChoice, setRatioChoice] = useState<string | null>(null);
  const [qualityChoice, setQualityChoice] = useState<'low' | 'medium' | 'high' | null>(null);

  const ratios = assistant?.ratios ?? [];
  // A chosen shape that has since been deleted on the settings screen falls
  // back rather than staying selected as a row that is no longer drawn.
  const ratio = ratioChoice && ratios.includes(ratioChoice) ? ratioChoice : (ratios[0] ?? '1:1');
  const quality = qualityChoice ?? assistant?.quality ?? 'medium';

  const fileInput = useRef<HTMLInputElement>(null);
  const cameraInput = useRef<HTMLInputElement>(null);
  const mediaInput = useRef<HTMLInputElement>(null);
  const input = useRef<HTMLDivElement>(null);
  const codeQuery = useRef<CodeQuery | null>(null);
  /** The caret as it was before the picker took focus away. */
  const savedRange = useRef<Range | null>(null);
  /**
   * The tiles still on the compose screen, by id.
   *
   * A ref rather than a look at `album`, because what asks is a read that
   * finished after the tile it was for was dropped — a stale closure would say
   * yes, and the object URL it then made would never be released. See fillIn.
   */
  const live = useRef(new Set<number>());

  const isMobile = useIsMobile();
  const isTouch = useIsTouch();
  const keyboardInset = useKeyboardInset();
  const frequent = useSmileyStats();
  const smileyBar = useSmileyBar();
  const quick = smileyBar ? frequent.slice(0, QUICK_COUNT) : [];

  /** Turns a finished take into the shape the upload wants. */
  const post = (recorded: Recorded, trim: TrimRange | null) =>
    onSendMedia({
      kind: recorded.mode === 'video' ? 'videoNote' : 'voice',
      blob: recorded.blob,
      fileName: fileNameFor(recorded.mode, recorded.mime),
      durationMs: recorded.durationMs,
      trimStartMs: trim?.startMs ?? null,
      trimEndMs: trim?.endMs ?? null,
      poster: recorded.poster,
    });

  const recorder = useRecorder({
    // Released without locking: this goes straight out, per the handoff.
    onSend: (recorded) => post(recorded, null),
    onTrim: setTrimming,
  });

  /**
   * Turns what came back from the system picker into tiles for the compose
   * screen, and appends them to whatever is already there.
   *
   * The tiles go up first and the videos fill themselves in, which is the whole
   * arrangement: a video's frame and its length are read on this device (see
   * lib/videoFrame.ts), and a minute-long clip off a phone takes a moment to
   * read. This used to be awaited before the screen was allowed to exist, so
   * picking one dropped the sender back into the conversation and left them
   * there, looking at nothing, until it was done. Now the screen is up by the
   * time the picker has finished closing, with a spinner on the tile that is
   * still being read and the send button held until it is not.
   *
   * A photo needs none of it — its own object URL is the picture.
   *
   * Anything that is not a photo or a video is simply not picked up: the input
   * asks for `image/*,video/*`, and a system picker that offers something else
   * anyway is not a reason to send the sender an error.
   */
  const addToAlbum = (files: File[]) => {
    const media = files.filter((file) => albumKindOf(file) !== null);
    if (media.length === 0) return;

    const { kept, dropped: over } = roomFor(album.length, media);
    setDropped(over);

    const items = kept.map((file): PickedItem => {
      const kind = albumKindOf(file)!;
      const id = nextPickId();
      live.current.add(id);
      // A video's tile is its grabbed frame, never the clip itself: an <img>
      // pointed at an MP4 draws the browser's broken-image glyph, which reads
      // as "this failed" rather than as "there is no picture for this yet".
      // Until the frame arrives there is deliberately no picture at all — the
      // placeholder and its spinner say what is happening.
      return kind === 'photo'
        ? {
            id,
            reading: false,
            file,
            kind,
            poster: null,
            durationMs: null,
            previewUrl: URL.createObjectURL(file),
          }
        : { id, reading: true, file, kind, poster: null, durationMs: null, previewUrl: null };
    });
    setAlbum((current) => [...current, ...items]);

    // All of them at once rather than one after another: the reads are mostly
    // spent waiting on slices of a file, and a group of clips picked together
    // should not fill in over the sum of their turns.
    for (const item of items) {
      if (item.reading) void readVideoFrame(item.file).then((frame) => fillIn(item.id, frame));
    }
  };

  /**
   * A clip that has finished being read, into the tile that was waiting for it.
   *
   * By id rather than by position, because both can have moved: the sender can
   * drop a tile or pick more while a read is still going. A tile that is gone by
   * the time its frame arrives takes the frame with it — and the object URL is
   * made here rather than inside the update, which has to stay something React
   * can run twice.
   */
  const fillIn = (id: number, frame: VideoFrame) => {
    if (!live.current.has(id)) return;
    const previewUrl = frame.poster ? URL.createObjectURL(frame.poster) : null;
    setAlbum((current) =>
      current.map((item) =>
        item.id === id
          ? { ...item, reading: false, poster: frame.poster, durationMs: frame.durationMs, previewUrl }
          : item,
      ),
    );
  };

  /** Drops the whole selection and everything held for it. */
  const clearAlbum = () => {
    for (const item of album) if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
    live.current.clear();
    setAlbum([]);
    setDropped(0);
    if (mediaInput.current) mediaInput.current.value = '';
  };

  /**
   * Recording is the touchscreen's, as the handoff draws it: a press-and-hold
   * gesture, which is a thing a thumb does and a mouse cannot. Where there is a
   * keyboard and a pointer the send button keeps its word — and both kinds of
   * recording still play in the thread wherever they were made.
   *
   * The width breakpoint would be the wrong test, and an iPad is where that
   * shows: wide enough to be laid out as a desktop, and still a device whose
   * only way to send a кружочек is this button.
   */
  // Not in a bot's chat: it answers a voice message and a кружочек with «Пока
  // понимаю только текст и фото», so the record button is an offer whose one
  // outcome is a refusal. The camera stays — a photograph is half of what this
  // bot is for.
  const recordable = isTouch && !editing && !staged && !canSend && !assistant && canRecord();

  /**
   * Pictures the browser put in the field itself, moved to where pictures go.
   *
   * The net under the paste handler, and on Android it is not a net but the
   * floor: a WebView hands a pasted picture over as markup rather than as a
   * file, so `pastedMedia` finds nothing, the default paste runs and an `<img>`
   * lands in the contentEditable. What that used to mean is a picture wedged in
   * the input — `serialise` understands only the smileys' images, so the
   * message read as empty, the send button lit up (it counts any image) and
   * pressing it did nothing whatsoever.
   *
   * Every image that is not a smiley is therefore taken out of the field the
   * moment it appears and handed to the album, which is where the same picture
   * would have gone had the clipboard offered it as a file. A drop onto the
   * field arrives the same way and is fixed by the same three lines.
   *
   * The field is cleared before the bytes are read, not after: reading can take
   * a fetch, and what must not happen is a picture sitting in the input while
   * somebody types around it.
   */
  const rescueStrays = () => {
    const el = input.current;
    if (!el) return;
    const strays = [...el.querySelectorAll<HTMLImageElement>('img:not([data-slug])')];
    if (strays.length === 0) return;

    for (const image of strays) image.remove();
    if (el.innerHTML === '<br>') el.innerHTML = '';

    void Promise.all(strays.map((image) => bytesBehind(image))).then((blobs) => {
      const files = blobs.filter((blob) => blob !== null).map((blob) => fileFromBlob(blob));
      if (files.length > 0) addToAlbum(files);
    });
  };

  const refresh = () => {
    const el = input.current;
    if (!el) return;
    // Before anything is measured: a picture the browser pasted in is not part
    // of the message and must not be counted as one.
    rescueStrays();
    // Browsers leave a stray <br> behind when the last character is deleted;
    // without this the placeholder never comes back.
    if (el.innerHTML === '<br>') el.innerHTML = '';
    setCanSend(el.textContent!.trim().length > 0 || el.querySelector('img') !== null);
    checkCommands(el);
  };

  /**
   * A leading «/» opens the command panel, narrowed by what follows it. Only
   * while the slash is still the whole message — once a space lands this is a
   * sentence that happens to start with one, and the panel gets out of the
   * way. Not while editing: the panel sends, and an edit saves.
   */
  const checkCommands = (el: HTMLElement) => {
    if (!commands?.length || editing) return setCommandMatches([]);
    const text = el.textContent!.replace(/\u200B/g, '');
    setCommandMatches(
      /^\/\S*$/.test(text)
        ? commands.filter((entry) => entry.command.startsWith(text.toLowerCase()))
        : [],
    );
  };

  const rememberCaret = () => {
    const el = input.current;
    const selection = window.getSelection();
    if (!el || !selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (el.contains(range.commonAncestorContainer)) savedRange.current = range.cloneRange();
  };

  const closeSuggestions = () => {
    codeQuery.current = null;
    setMatches([]);
    setActive(0);
  };

  /**
   * Reads the text immediately before the caret. Driven by the live selection
   * rather than the whole message, so a `:code` typed in the middle of a
   * sentence suggests just the same.
   */
  const checkSuggestions = () => {
    const el = input.current;
    const selection = window.getSelection();
    if (smileysOpen || !el || !selection?.rangeCount) return closeSuggestions();

    const range = selection.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE || !el.contains(node)) return closeSuggestions();

    const typed = (node.nodeValue ?? '').slice(0, range.startOffset);
    const found = TYPED_CODE.exec(typed);
    if (!found) return closeSuggestions();

    const suggestions = suggestSmileys(found[1]!);
    if (suggestions.length === 0) return closeSuggestions();

    codeQuery.current = {
      node: node as Text,
      start: range.startOffset - found[0].length,
      end: range.startOffset,
    };
    setMatches(suggestions);
    setActive(0);
  };

  /** Drops an image at the caret, or at the end when the caret was lost. */
  const insert = (slug: string) => {
    const el = input.current;
    const smiley = findSmiley(slug);
    if (!el || !smiley) return;

    /**
     * On a phone, picking a smiley must not raise the keyboard. Focusing a
     * contentEditable is what raises it, and so is handing the live selection
     * a range inside one — which is the subtle half: the caret has to be moved
     * to insert at all. So when the field does not already have focus, the
     * image is placed through the stored Range alone and the selection is left
     * untouched; the caret is restored later, by whoever asks for the keyboard.
     *
     * The keyboard then comes up when it is asked for — a tap on the field or
     * on the keyboard icon — and never as a side effect of choosing a smiley.
     */
    const quietly = isMobile && document.activeElement !== el;
    if (!quietly) el.focus();

    let range = savedRange.current;
    if (!range || !el.contains(range.commonAncestorContainer)) {
      range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
    }

    const img = smileyImage(smiley);
    range.deleteContents();
    range.insertNode(img);
    range.setStartAfter(img);
    range.collapse(true);
    savedRange.current = range.cloneRange();

    if (!quietly) {
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }

    bumpSmiley(slug);
    closeSuggestions();
    refresh();
  };

  /** Accepts a suggestion by replacing the `:code` that produced it. */
  const acceptSuggestion = (smiley: Smiley) => {
    const el = input.current;
    const query = codeQuery.current;
    if (!el || !query?.node.parentNode) return;

    const text = query.node.nodeValue ?? '';
    const tail = text.slice(query.end);
    query.node.nodeValue = text.slice(0, query.start);

    const img = smileyImage(smiley);
    // A text node after the image gives the caret somewhere to sit; the
    // zero-width space keeps it from collapsing away when the tail is empty.
    const after = document.createTextNode(tail || '\u200B');
    query.node.parentNode.insertBefore(img, query.node.nextSibling);
    query.node.parentNode.insertBefore(after, img.nextSibling);

    const range = document.createRange();
    range.setStart(after, tail ? 0 : 1);
    range.collapse(true);
    const selection = window.getSelection();
    selection?.removeAllRanges();
    selection?.addRange(range);
    savedRange.current = range.cloneRange();

    el.focus();
    bumpSmiley(smiley.slug);
    closeSuggestions();
    refresh();
  };

  /** The panel's ⌫, which has no keyboard behind it to do the job. */
  const backspace = () => {
    const el = input.current;
    const last = el?.lastChild;
    if (!el || !last) return;

    if (last.nodeType === Node.TEXT_NODE && (last.nodeValue?.length ?? 0) > 1) {
      last.nodeValue = last.nodeValue!.slice(0, -1);
    } else {
      el.removeChild(last);
    }
    savedRange.current = null;
    refresh();
  };

  const clear = () => {
    const el = input.current;
    if (el) el.innerHTML = '';
    closeSuggestions();
    setCommandMatches([]);
    savedRange.current = null;
    setCanSend(false);
  };

  /** Puts the keyboard back: focusing the input is what actually raises it. */
  const showKeyboard = () => {
    setSmileysOpen(false);
    const el = input.current;
    if (!el) return;

    el.focus();
    // Typing carries on after the smileys just inserted rather than wherever
    // the browser decides to put the caret in a field it has not focused yet.
    const range = savedRange.current;
    if (range && el.contains(range.commonAncestorContainer)) {
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };

  function submit(event?: FormEvent) {
    event?.preventDefault();
    const el = input.current;
    const body = el ? serialise(el) : '';

    if (editing) {
      if (body) onSaveEdit(editing.id, body);
      // An emptied message is not a delete — the menu has one of those, and
      // silently deleting on save is not something anyone asked for.
      else onCancelEdit();
      clear();
      return;
    }

    if (!staged && !body) return;

    // An attachment and a caption go as two messages — one bubble each, as in
    // the design, where a file bubble never carries text.
    if (staged) onSendFile(staged);
    // The mode row travels only where it means something: a message to a person
    // has no second argument at all, and never had one.
    if (body) {
      if (assistant) onSendText(body, { mode, ratio, quality });
      else onSendText(body);
    }

    setStaged(null);
    if (fileInput.current) fileInput.current.value = '';
    clear();
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (matches.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setActive((current) => (current + 1) % matches.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setActive((current) => (current - 1 + matches.length) % matches.length);
        return;
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        event.preventDefault();
        acceptSuggestion(matches[active]!);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        closeSuggestions();
        return;
      }
    }
    if (event.key === 'Escape' && editing) {
      event.preventDefault();
      onCancelEdit();
      return;
    }
    // On a phone the return key is a new paragraph and sending is the button's
    // job, as the handoff asks. On a desktop Enter has always sent, and taking
    // that away to satisfy a mobile screen would be a downgrade — so Shift+Enter
    // is the desktop's paragraph, and the browser's own default makes it.
    if (event.key === 'Enter' && !event.shiftKey && !isMobile) {
      event.preventDefault();
      submit();
    }
  }

  // A picture pasted while the field does not have the focus.
  //
  // Ctrl+V is aimed at the conversation, not at an element — somebody who has
  // just scrolled the thread or clicked a bubble has taken the focus out of the
  // field without meaning to leave the composer, and a paste that does nothing
  // then reads as the feature being broken. This is the same handler one level
  // out, and it only ever takes what the field's own did not: `defaultPrevented`
  // is how a paste the field already dealt with says so, and a target that is
  // itself editable — the search field, a dialog's input — owns its own paste.
  //
  // Text is deliberately not handled here. Inserting it needs a caret, and
  // there is none outside the field; a picture needs nowhere to go but the
  // compose screen.
  useEffect(() => {
    const onWindowPaste = (event: ClipboardEvent) => {
      if (event.defaultPrevented || !event.clipboardData) return;
      const target = event.target;
      const editable =
        target instanceof HTMLElement &&
        (target.isContentEditable || target.tagName === 'INPUT' || target.tagName === 'TEXTAREA');
      if (editable) return;
      // Both of these are screens of their own, over the composer, with their
      // own idea of what is being sent.
      if (cameraOpen || trimming) return;

      const media = pastedMedia(event.clipboardData);
      if (media.length === 0) return;
      event.preventDefault();
      addToAlbum(media);
    };
    window.addEventListener('paste', onWindowPaste);
    return () => window.removeEventListener('paste', onWindowPaste);
    // addToAlbum is rebuilt every render and reads the selection it is adding
    // to, so the listener has to be re-bound when that changes — hence the
    // album rather than the function in the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [album, cameraOpen, trimming]);

  // Opening the grid closes the suggestions, and vice versa: two overlapping
  // popups over one input would fight for the same corner and the same Enter.
  // The attach menu shares that corner, so it goes the same way.
  useEffect(() => {
    if (smileysOpen) {
      closeSuggestions();
      setAttachOpen(false);
    }
  }, [smileysOpen]);

  // A reply is answered on the spot, so the keyboard comes up with the banner —
  // what a tap on the field would have done next anyway, and what makes the
  // swipe worth having over the menu. Nothing is loaded into the input: a
  // half-written line simply becomes the reply.
  useEffect(() => {
    if (replying) showKeyboard();
    // Only a new target reaches for the keyboard; re-rendering with the same
    // one must not steal focus back from wherever the reader has put it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replying?.id]);

  // Starting an edit loads the message in; cancelling or finishing empties the
  // input again, so a half-written correction never leaks into the next message.
  useEffect(() => {
    const el = input.current;
    if (!el) return;
    if (editing) {
      deserialise(el, editing.body);
      setSmileysOpen(false);
      el.focus();
      const range = document.createRange();
      range.selectNodeContents(el);
      range.collapse(false);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
      savedRange.current = range.cloneRange();
      refresh();
    } else if (el.innerHTML !== '') {
      clear();
    }
    // Only the identity of the message being edited should reload the input —
    // reacting to its body would fight the reader for every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editing?.id]);

  return (
    <>
      <form className="composer" onSubmit={submit}>
        {/* A denied permission, a camera another app is holding: the reason
            belongs above the bar, not inside a pill that never appeared. In its
            own element so that it may wrap — a failed stitch says what the
            encoder refused and how big the pieces were, which is a sentence
            rather than a phrase (see stitch in lib/capture.ts). */}
        {recorder.error && (
          <div className="record-error" role="alert">
            <span>{recorder.error}</span>
            <button type="button" onClick={recorder.dismissError}>
              Понятно
            </button>
          </div>
        )}

        {staged && (
          <div className="composer-chip">
            {staged.name} · {formatFileSize(staged.size)}
            <button
              type="button"
              className="icon-button composer-chip-remove"
              onClick={() => {
                setStaged(null);
                if (fileInput.current) fileInput.current.value = '';
              }}
              aria-label="Убрать файл"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* Both cannot be up at once — starting one clears the other in
            ChatScreen — and the reply banner is the same shape as the edit one
            so the bar does not change height between them. */}
        {replying && !editing && (
          <div className="composer-editing">
            <Reply className="composer-editing-icon" size={15} />
            <div className="composer-editing-text">
              <div className="composer-editing-title">Ответ {replying.name}</div>
              <div className="composer-editing-preview">{replying.preview}</div>
            </div>
            <button
              type="button"
              className="composer-editing-cancel"
              onClick={onCancelReply}
              aria-label="Отменить ответ"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {editing && (
          <div className="composer-editing">
            <Pencil className="composer-editing-icon" size={15} />
            <div className="composer-editing-text">
              <div className="composer-editing-title">Редактирование сообщения</div>
              <div className="composer-editing-preview">{editing.body}</div>
            </div>
            <button
              type="button"
              className="composer-editing-cancel"
              onClick={onCancelEdit}
              aria-label="Отменить редактирование"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {/* The floating grid is the desktop shape. On a phone the panel below
            takes the keyboard's place instead — see SmileyPanel. */}
        {smileysOpen && !isMobile && (
          <SmileyPicker onPick={insert} onClose={() => setSmileysOpen(false)} />
        )}

        {attachOpen && (
          <AttachMenu
            onCamera={() => {
              setAttachOpen(false);
              setCameraOpen(true);
            }}
            onGallery={() => {
              setAttachOpen(false);
              mediaInput.current?.click();
            }}
            onFile={() => {
              setAttachOpen(false);
              fileInput.current?.click();
            }}
            onClose={() => setAttachOpen(false)}
          />
        )}

        {/* Same slot the smiley suggestions use; the two cannot be up at once,
            since one wants the message to start with «/» and the other a
            «:code» before the caret. A tap sends the command outright — they
            take no arguments, so there is nothing left to type. */}
        {commandMatches.length > 0 && (
          <div className="composer-suggest composer-commands" role="listbox" aria-label="Команды">
            {commandMatches.map((entry) => (
              <button
                key={entry.command}
                type="button"
                role="option"
                className="composer-suggest-row"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  // A command is answered from the ledger, never by a model, so
                  // the mode row has nothing to say about it.
                  onSendText(entry.command);
                  clear();
                }}
              >
                <code>{entry.command}</code>
                <span>{entry.description}</span>
              </button>
            ))}
          </div>
        )}

        {matches.length > 0 && (
          <div className="composer-suggest" role="listbox" aria-label="Смайлы по коду">
            {matches.map((smiley, index) => (
              <button
                key={smiley.slug}
                type="button"
                role="option"
                aria-selected={index === active}
                className={`composer-suggest-row${index === active ? ' composer-suggest-active' : ''}`}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActive(index)}
                onClick={() => acceptSuggestion(smiley)}
              >
                <img src={smileyUrl(smiley.slug)} alt="" />
                <code>:{smiley.slug}:</code>
                <span>{smiley.label}</span>
              </button>
            ))}
          </div>
        )}

        {/* Gone entirely rather than emptied when the setting is off: an empty
            row would still hold its height and its border above the bar. */}
        {quick.length > 0 && (
          <div className="composer-quick">
            {quick.map((smiley) => (
              <button
                key={smiley.slug}
                type="button"
                className="composer-quick-button"
                title={smiley.label}
                aria-label={smiley.label}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => insert(smiley.slug)}
              >
                <img src={smileyUrl(smiley.slug)} alt="" />
              </button>
            ))}
          </div>
        )}

        {/* Both rows live above the input row and only while they are wanted:
            the segment while a mode is being chosen, the parameters while the
            mode is «Картинка». A row that were always there would push the
            field up the screen in every chat with a bot in it, which is most of
            what «не мешать, пока не спросили» means here. */}
        {assistant && modeOpen && (
          <div className="composer-modes" role="radiogroup" aria-label="Режим ответа">
            {(['auto', 'chat', 'image'] as const).map((option) => (
              <button
                key={option}
                type="button"
                role="radio"
                aria-checked={mode === option}
                className={`composer-mode${mode === option ? ' composer-mode-on' : ''}`}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  setMode(option);
                  // Collapses back to the chip on anything but «Картинка»,
                  // which has a row of its own to show underneath.
                  if (option !== 'image') setModeOpen(false);
                }}
              >
                <ModeIcon mode={option} />
                {MODE_LABELS[option]}
              </button>
            ))}
          </div>
        )}

        {assistant && mode === 'image' && (
          <div className="composer-params">
            <div className="composer-param">
              <span className="composer-param-label">Формат</span>
              <div className="composer-param-choices">
                {assistant.ratios.map((option) => (
                  <button
                    key={option}
                    type="button"
                    aria-pressed={ratio === option}
                    className={`composer-chip${ratio === option ? ' composer-chip-on' : ''}`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => setRatioChoice(option)}
                  >
                    <RatioGlyph ratio={option} />
                    {option}
                  </button>
                ))}
              </div>
            </div>

            {/* Only where the provider has one. Gemini's image endpoint takes
                no quality at all — three buttons there would be three ways to
                get the same picture at the same price, which is worse than the
                setting being absent: it reads as broken rather than as
                inapplicable. See shapeHint in server/src/gemini.ts. */}
            {assistant.provider === 'openai' && (
              <div className="composer-param">
                <span className="composer-param-label">Качество</span>
                <div className="composer-param-choices">
                  {QUALITIES.map(([value, label]) => (
                    <button
                      key={value}
                      type="button"
                      aria-pressed={quality === value}
                      className={`composer-chip${quality === value ? ' composer-chip-on' : ''}`}
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={() => setQualityChoice(value)}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {/* The one place the price shows up, and it shows up as an ordering
                rather than as a number: what somebody needs to know is which
                end to iterate at, not what a picture costs to a hundredth of a
                cent. The figures are in /admin, where they are somebody's job.

                The other bot gets the sentence it has earned instead: its shape
                is a line added to the prompt rather than a parameter, so it is
                followed most of the time and not always, and saying so beats
                somebody deciding the control is broken. */}
            <p className="composer-param-hint">
              {assistant.provider === 'openai'
                ? 'Черновик — для итераций, максимум — для финала'
                : 'Формат — пожелание: эта модель не принимает размер и следует ему не всегда'}
            </p>
          </div>
        )}

        <div className="composer-row">
          <input
            ref={fileInput}
            type="file"
            hidden
            onChange={(event) => setStaged(event.target.files?.[0] ?? null)}
          />
          {/* A second input purely for `capture`: the same one cannot both open
              the picker and go straight to the camera. This is the fallback for
              a phone that will not hand this page a stream — what it brings back
              is a photograph, so it goes where photographs go. */}
          <input
            ref={cameraInput}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={(event) => {
              const files = [...(event.target.files ?? [])];
              event.target.value = '';
              void addToAlbum(files);
            }}
          />
          {/* And a third for the album: `multiple`, and narrowed to what a tile
              can hold so the system picker opens on the camera roll rather than
              on the file system. Cleared on every change — picking the same
              photograph twice in a row has to fire `change` both times. */}
          <input
            ref={mediaInput}
            type="file"
            accept="image/*,video/*"
            multiple
            hidden
            onChange={(event) => {
              const files = [...(event.target.files ?? [])];
              event.target.value = '';
              void addToAlbum(files);
            }}
          />

          {/* The pill steps aside for the recording pill, which is the same
              shape: the bar must not change height when a take starts. */}
          {recorder.recording ? (
            <RecordPill recording={recorder.recording} />
          ) : (
            <div className="composer-pill">
            {/* Inside the field rather than above it: in «Авто» — where it sits
                until somebody says otherwise — it costs the bar no height at
                all, and the composer in the bot's chat is the composer
                everywhere else with one extra word in it. */}
            {assistant && !modeOpen && (
              <button
                type="button"
                className="composer-mode-chip"
                aria-label="Режим ответа"
                aria-expanded={false}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => setModeOpen(true)}
              >
                <ModeIcon mode={mode} />
                {MODE_LABELS[mode]}
              </button>
            )}
            {/* Not in a bot's chat. Smileys are how you say something to a
                person without words, and there is nobody on the other end to
                read one — the slot at the head of the field belongs to the mode
                chip there, and two round buttons in a row would be one too many
                on a phone. The codes still work if somebody types one. */}
            {!assistant && (
              <button
                type="button"
                className={`composer-smile${smileysOpen ? ' composer-smile-open' : ''}`}
                title="Смайлы"
                aria-label={smileysOpen && isMobile ? 'Клавиатура' : 'Смайлы'}
                aria-expanded={smileysOpen}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  if (smileysOpen) showKeyboard();
                  else {
                    // Dismissing the keyboard first is what frees the slot the
                    // panel is about to occupy at exactly the same height.
                    if (isMobile) input.current?.blur();
                    setSmileysOpen(true);
                  }
                }}
              >
                <img src={smileyUrl('smile')} alt="" />
              </button>
            )}

            {/* contentEditable rather than an input: a smiley is an inline image
                sitting in the same line as the text being typed. */}
            <div
              ref={input}
              className="composer-input"
              contentEditable
              suppressContentEditableWarning
              role="textbox"
              aria-multiline="true"
              aria-label="Сообщение"
              data-placeholder="Сообщение"
              onInput={() => {
                rememberCaret();
                checkSuggestions();
                refresh();
                onTyping();
              }}
              onKeyDown={onKeyDown}
              onKeyUp={rememberCaret}
              onMouseUp={rememberCaret}
              onBlur={rememberCaret}
              // Tapping the field means going back to typing, so the panel gets
              // out of the keyboard's way. This listens for the tap rather than
              // for focus: inserting a smiley moves the caret, and moving the
              // caret focuses a contentEditable, which would close the panel
              // after every pick — the one thing it is meant to stay open for.
              onPointerDown={() => {
                if (isMobile) setSmileysOpen(false);
              }}
              // Plain text only: pasted markup would bring its own fonts and
              // colours into a bubble that has its own. Its line breaks stay —
              // pasting an address or a list is exactly the case that needs
              // them — normalised to `\n`, since Windows clipboards carry CRLF.
              //
              // A clipboard we cannot read at all keeps the browser's own paste
              // rather than being cancelled into nothing: serialise walks the
              // tree and takes the text out of whatever lands, so the worst case
              // is markup that looks wrong for as long as it is being typed.
              onPaste={(event) => {
                // Pictures first, and text only when there are none: a
                // clipboard that carries both is a paste of text (see
                // lib/paste.ts). What comes off it goes through addToAlbum, so
                // a pasted screenshot gets the compose screen, the caption and
                // the grouping a picked photograph gets.
                const media = pastedMedia(event.clipboardData);
                if (media.length > 0) {
                  event.preventDefault();
                  addToAlbum(media);
                  return;
                }

                const text = pastedText(event.clipboardData);
                if (!text) return;
                event.preventDefault();
                if (!document.execCommand('insertText', false, text)) insertAtCaret(text);
                // execCommand raises `input` and this is already done; the
                // fallback mutates the DOM directly and raises nothing.
                rememberCaret();
                refresh();
              }}
            />

            {/* One button for everything that can be attached. Photos, the
                camera and documents used to sit here as three, which is three
                decisions in the bar before the one anybody is actually making;
                what they open is one menu now — see AttachMenu.

                Gone from the pill while a phone has typing in it: those are
                the characters that need the width, and attaching mid-sentence
                is rare enough to cost a Backspace. A desktop pill has room for
                both, so there the button stays. */}
            {!(isMobile && canSend) && (
            <button
              type="button"
              className={`icon-button composer-attach${attachOpen ? ' composer-attach-open' : ''}`}
              title="Вложение"
              aria-label="Вложение"
              aria-expanded={attachOpen}
              onClick={() => {
                if (attachOpen) return setAttachOpen(false);
                // The menu takes the same slot above the bar the smileys use,
                // and on a phone it needs the keyboard out of the way first.
                setSmileysOpen(false);
                if (isMobile) input.current?.blur();
                setAttachOpen(true);
              }}
            >
              <Paperclip size={17} />
            </button>
            )}
            </div>
          )}

          {recordable ? (
            <RecordButtons recorder={recorder} />
          ) : (
            <button
              className="composer-send"
              type="submit"
              disabled={!staged && !canSend}
              aria-label={editing ? 'Сохранить' : 'Отправить'}
            >
              {editing ? 'Сохранить' : 'Отправить'}
            </button>
          )}
        </div>
      </form>

      {/* What the camera sees, for as long as it is recording: the handoff asks
          for the framing to be answerable during the take and not only at the
          trim screen. Voice takes have nothing to show.

          Outside the bar rather than inside it, though it belongs to the same
          recording: it dims everything the bar is not, and a scrim nested inside
          the thing it must leave lit would take the bar's own background down
          with it. */}
      {recorder.recording?.mode === 'video' && recorder.preview && (
        <RecordPreview
          source={recorder.preview}
          facing={recorder.facing}
          canFlip={recorder.canFlip}
          onFlip={recorder.flip}
          elapsedMs={recorder.recording.elapsedMs}
          flipping={recorder.recording.flipping}
        />
      )}

      {/* What is filmed here is not treated as having been filmed here: a
          photograph and a clip both go into the album, which is where a picked
          one goes, so the compose screen, the size check and the grouping are
          the same code for both. */}
      {cameraOpen && (
        <CameraScreen
          maxUploadBytes={maxUploadBytes}
          onCapture={(file) => {
            setCameraOpen(false);
            void addToAlbum([file]);
          }}
          // Except a кружочек, which is not an attachment: it lands on the same
          // trim screen a locked composer take does, and leaves through the
          // same post() — its own message, no caption, no grouping.
          onNote={(recorded) => {
            setCameraOpen(false);
            setTrimming(recorded);
          }}
          onGallery={() => {
            setCameraOpen(false);
            mediaInput.current?.click();
          }}
          // `capture` is a phone's word: on a desktop it is ignored and the
          // input opens an ordinary file picker, which is not what a button
          // offering the camera should do.
          onSystemCamera={
            isTouch
              ? () => {
                  setCameraOpen(false);
                  cameraInput.current?.click();
                }
              : null
          }
          onClose={() => setCameraOpen(false)}
        />
      )}

      {/* Up for exactly as long as there is a selection: dropping the last tile
          is what closes it, since an empty compose screen is not a state. */}
      {album.length > 0 && (
        <AlbumCompose
          items={album}
          dropped={dropped}
          onAdd={() => mediaInput.current?.click()}
          onRemove={(index) => {
            const going = album[index];
            if (going) live.current.delete(going.id);
            if (going?.previewUrl) URL.revokeObjectURL(going.previewUrl);
            setDropped(0);
            setAlbum((current) => current.filter((_, at) => at !== index));
          }}
          maxUploadBytes={maxUploadBytes}
          onCancel={clearAlbum}
          onSend={(outgoing) => {
            // The object URLs are handed on with the message and released once
            // the server has answered (see sendAlbum) — the bubble is drawing
            // from them until then.
            onSendAlbum(outgoing);
            live.current.clear();
            setAlbum([]);
            setDropped(0);
            if (mediaInput.current) mediaInput.current.value = '';
          }}
        />
      )}

      {trimming && (
        <TrimScreen
          recorded={trimming}
          facing={recorder.facing}
          canFlip={recorder.canFlip}
          onFlip={recorder.flip}
          onDiscard={() => setTrimming(null)}
          onSend={(trim) => {
            post(trimming, trim);
            setTrimming(null);
          }}
        />
      )}

      {smileysOpen && isMobile && (
        <SmileyPanel
          height={keyboardInset}
          reacting={false}
          onPick={insert}
          onKeyboard={showKeyboard}
          onBackspace={backspace}
          onCancelReaction={() => setSmileysOpen(false)}
        />
      )}
    </>
  );
}
